export const MAX_ORPHAN_SESSIONS_TO_RESTORE = 10;

export interface OrphanPtySession {
	id?: string;
	sessionId?: string;
	createdAt: number;
	lastActivity: number;
	bufferBytes?: number;
	connected?: boolean;
}

interface SplitNode {
	type?: string;
	ptySessionId?: unknown;
	children?: SplitNode[];
}

interface TabLike {
	ptySessionId?: unknown;
	splitTree?: SplitNode;
}

export interface TerminalWindowLike {
	appId?: string;
	meta?: {
		ptySessionId?: unknown;
		tabs?: unknown;
	};
}

export function getOrphanSessionMessage(count: number): string {
	if (count === 1) {
		return "Terminal cleanup available: 1 session is still running from a previous visit";
	}
	return `Terminal cleanup available: ${count} sessions are still running from a previous visit`;
}

export function shouldOfferOrphanSessionRestore(_count: number): boolean {
	// Restoring an orphan requires TerminalApp to attach to existing PTYs.
	// It currently spawns fresh PTYs, so keep restore hidden until attach mode exists.
	return false;
}

export function normalizePtySessions(
	sessions: OrphanPtySession[],
): RequiredSession[] {
	return sessions
		.map((session) => ({
			...session,
			sessionId: session.sessionId ?? session.id,
		}))
		.filter(
			(session): session is RequiredSession =>
				typeof session.sessionId === "string" && session.sessionId.length > 0,
		);
}

export function getActiveTerminalSessionIds(
	windows: TerminalWindowLike[],
): Set<string> {
	const activeSessions = new Set<string>();

	for (const window of windows) {
		if (window.appId !== "terminal") continue;

		if (typeof window.meta?.ptySessionId === "string") {
			activeSessions.add(window.meta.ptySessionId);
		}

		if (Array.isArray(window.meta?.tabs)) {
			for (const tab of window.meta.tabs as TabLike[]) {
				if (typeof tab.ptySessionId === "string") {
					activeSessions.add(tab.ptySessionId);
				}
				if (tab.splitTree) {
					collectSplitTreeSessionIds(tab.splitTree, activeSessions);
				}
			}
		}
	}

	return activeSessions;
}

export function findOrphanPtySessions(
	sessions: OrphanPtySession[],
	windows: TerminalWindowLike[],
): RequiredSession[] {
	const normalized = normalizePtySessions(sessions);
	const activeSessions = getActiveTerminalSessionIds(windows);
	return normalized.filter((session) => !activeSessions.has(session.sessionId));
}

function collectSplitTreeSessionIds(
	node: SplitNode,
	activeSessions: Set<string>,
): void {
	if (node.type === "leaf" && typeof node.ptySessionId === "string") {
		activeSessions.add(node.ptySessionId);
		return;
	}

	if (node.type === "branch" && Array.isArray(node.children)) {
		for (const child of node.children) {
			collectSplitTreeSessionIds(child, activeSessions);
		}
	}
}

type RequiredSession = OrphanPtySession & { sessionId: string };
